package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/context_cancellation")
@Composable
fun ContextCancellationPage() {
    BlogLayout(
        PageProperties(
            "Context | Cancellation",
            "Why Use Scopes, Coroutine Scope, Supervisor Scope, suspendCoroutine, suspendCancellableCoroutine, Coroutine context, Job"
        ),
        BlogProperties("Coroutines", "Coroutine Context | Coroutine Cancellation")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Creates a CoroutineScope that wraps the given coroutine context. If the given context does not contain a Job element. An Executing asynchronous tasks is essential to keep the interface responsive and avoid blocking the main thread. The use of Coroutines simplifies this process, and a fundamental concept within them is the Job, which represents a unit of work that can be controlled and canceled.",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Why Use Scopes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scopes avoid unnecessary re-creation of instances, saving resources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They align dependency instances with component lifecycles, preventing memory leaks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scopes provide a straightforward way to share instances across different parts of the app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Note",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModelScope → property on ViewModel",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "lifecycleScope → property on LifecycleOwner",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "GlobalScope → singleton object",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "coroutineScope{} → is a suspending function defined in kotlinx.coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "GlobalScope (not recommended)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "GlobalScope is a global coroutine scope that launches top-level coroutines that are not bound to any specific lifecycle. Coroutines launched in GlobalScope are long-running and live until the application is terminated or they complete their execution. This can lead to memory leaks and uncontrolled coroutines because there is no automatic cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    GlobalScope.launch {
                        delay(1000)
                        println("Running in GlobalScope")
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While this is valid, it’s not recommended, especially in Android applications, because the coroutines will continue to run even when the associated activity or component is destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutine Scope",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope provides a structured way to manage coroutines, ensuring that they respect the lifecycle of the scope they are launched in. It is tied to a lifecycle, such as an Activity or ViewModel in Android, and ensures coroutines are canceled when the lifecycle is destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope is an interface to create a new coroutine scope provides a structured concurrency mechanism that allows developers to launch and manage coroutines within a specific scope, such as a fragment or activity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is usually defined within a function or class, and coroutines initialized in that scope run for the lifetime of that scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope is used to define the scope of a specific process or workflow. By creating a CoroutineScope, you can start, cancel, and manage coroutines within that scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It define the Lifetime of coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Alternatively, developers can use the GlobalScope object, which provides a global scope for launching coroutines that can be used throughout the entire application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They ensure that an instance of a dependency lives for a specific duration, which can be tied to an activity, fragment, view, or the application itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Two important elements: `launch` and `async`. These functions are used to initiate coroutines and begin their execution within the defined scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is important to note that every coroutine must be launched in a coroutine scope, as it provides a context for the coroutine to run in, and ensures that the coroutine is properly canceled when the scope is canceled or completed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A CoroutineScope provides a structured way to launch and manage coroutines, and it ensures that all coroutines launched within that scope are cancelled when the associated Android component is destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This helps prevent memory leaks and ensures that no dangling coroutines continue to execute after the UI component is no longer active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutine Scope Responsibilities",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Manage Coroutines’ Lifecycle: All coroutines started in a scope are bound to it. If the scope is canceled, all child coroutines are also canceled automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Provide a Context: A scope defines the coroutine’s context, including the dispatcher (the thread on which the coroutine runs) and other coroutine context elements like exception handling.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are multiple ways to create and use a CoroutineScope. You can either define your own custom scope or use predefined ones like GlobalScope, viewModelScope, or lifeCycleScope in Android.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "coroutineScope - Propagating Failures to All Children",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The coroutineScope builder is one of the core tools in structured concurrency. When a child coroutine inside coroutineScope fails, it cancels all other child coroutines and propagates the failure to the parent coroutine. (Coroutine scope will cancel all child coroutines and all of its work if there is any error within the scope.) This strict failure handling ensures that if one part of your operation fails, no other child coroutine continues running in an inconsistent or invalid state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It creates a parent-child relationship between the scope and its coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Why Does coroutineScope Cancel All Children on Failure? coroutineScope's behavior is straightforward: all children of the scope represent a single unit of work. If one coroutine fails, it compromises the integrity of the entire scope. Cancelling the remaining coroutines ensures that no partial or invalid results are processed. This behavior aligns with Kotlin’s goal of ensuring predictability and consistency in concurrent programming.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Supervisor Scope - Fail Independently (If one child fails, others continue unaffected)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "supervisorScope isolates failures, allowing child coroutines to fail independently. supervisorScope when you need resilience in the face of partial failure. Each coroutine can operate independently, and failure in one doesn't affect others.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike a SupervisorJob, which only supervises its direct children in a CoroutineScope, supervisorScope {} is a special coroutine builder that ensures full failure isolation for all coroutines within the scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a child coroutine inside supervisorScope fails, it does not cancel other child coroutines and does not propagate the failure to the parent coroutine. (Supervisor scope will continue to complete the tasks within the scope and won’t cancel other child coroutines when one of them fails.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A SupervisorJob is a type of Job that is used to create a supervisory context for coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you create a SupervisorJob and use it as the parent for coroutines, it changes the behavior of coroutine cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If a child coroutine of a SupervisorJob is cancelled, it does not automatically cancel its siblings.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you create a SupervisorJob and use it as the parent for coroutines, it changes the behavior of coroutine cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The key distinction between supervisorScope and a CoroutineScope with SupervisorJob is that within a supervisorScope, failures are isolated without the need for additional exception handling at the coroutine level. Any child coroutine’s failure does not propagate to its siblings or the parent scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "supervisorScope - Real-World Analogy: Rajasthan's Water Management System",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine a stepwell (baori) in Rajasthan, where multiple water storage sections exist. If one section collapses, the others continue functioning without affecting the whole system. Similarly, supervisorScope ensures that if one coroutine fails, the others continue executing independently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Another analogy is Rajasthan's district-level irrigation management—each district has its own water reserves, and failure in one area doesn’t lead to complete system collapse, unlike a centralized supply.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A user books a Desert Safari and a City Palace Tour together. If the Desert Safari fails due to weather conditions, the City Palace Tour should still proceed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutine Context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A CoroutineContext is an immutable set of elements that defines the behavior and characteristics of a coroutine. It acts as the environment or configuration for a coroutine, determining how and where it runs, its name, and how its lifecycle is managed. It includes things like dispatchers, exception handlers, and coroutine name. The context is used to determine how and where the coroutine will be executed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The CoroutineContext interface represents an element or a collection of elements, conceptually similar to a map or set collection. It acts as an indexed set of Element instances, such as Job, CoroutineName, and CoroutineDispatcher. Each CoroutineContext.Element is itself a CoroutineContext, allowing multiple elements to be combined. This structure means that CoroutineContext implements the Composite design pattern.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the case of a coroutine, context designates what task executes where. Managing context is important - switching what task executes on what thread or thread pool gives capacity to tweak concurrency behavior. Context be used from the Dispatchers.kt class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The CoroutineContext provides the context in which a coroutine runs, influencing its behavior and execution characteristics.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Coroutine context is at the core of every coroutine. Every coroutine is lied to a specific coroutine context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine context is represented by the `CoroutineContext` interface. The `CoroutineScope` interface inherits from CoroutineContext and is commonly used to create and manage coroutines.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It tells us that thread will execute on which threads.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Represents the context of a coroutine, including its coroutine dispatcher and other elements such as coroutine name and exception handler.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Can be customized using extension functions or by creating custom implementations of CoroutineContext.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    fun main() {
                        val ctx: CoroutineContext = CoroutineName("A name")

                        val coroutineName: CoroutineName? = ctx[CoroutineName]
                        // or ctx.get(CoroutineName)
                        println(coroutineName?.name)
                        val job: Job? = ctx[Job] // or ctx.get(Job)
                        println(job) // null
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    val currentCoroutineContext = coroutineContext

                     println("1. Current Coroutine's Context (runBlocking): $currentCoroutineContext")
                     println("- Job in context: ${currentCoroutineContext[Job]}")
                     println("- Dispatcher in context: ${currentCoroutineContext[CoroutineDispatcher]}")
                     println("- Name in context: ${currentCoroutineContext[CoroutineName]}") // Likely null if not explicitly set

                    // Create a new context by combining a Dispatcher and a Name
                    val customContext = Dispatchers.IO + CoroutineName("MyBackgroundWorker")
                    println("\n2. Custom Context created: $customContext")

                    // Launch a new coroutine using our custom context
                    launch(customContext) {
                        println("\n - Coroutine launched with custom context -")
                        println(" Running on thread: ${Thread.currentThread().name}")
                        println(" My Job: ${coroutineContext[Job]}")
                        println(" My Dispatcher: ${coroutineContext[CoroutineDispatcher]}")
                        println(" My Name: ${coroutineContext[CoroutineName]?.name}")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Coroutine Context Characteristics",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Set of Elements: CoroutineContext is an immutable collection of CoroutineContext.Element instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Immutability: When you “add” elements to a context using the + operator, you do not change the original context. You get a brand new CoroutineContext that combines the elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition: It is how different aspects of a coroutine’s behavior (like where it runs, its name, or how it is cancelled) are bundled together.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }


            SpanText(
                text = "How can CoroutineContext be used?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Within Coroutine builder extension functions like launch(context). CoroutineContext is an interface in Kotlin's coroutines that helps us define the context or the environment in which a coroutine executes, using various elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope(context) — context defined within a CoroutineScope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "withContext(context) — as a way to swap context within a specific coroutine",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is coroutineContext in Kotlin Coroutines? (CoroutineContext.Element: The Building Blocks of Context)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " A CoroutineContext.Element is an individual component that constitutes a CoroutineContext. Each element holds a specific piece of information or behavior for a coroutine and is identified by a unique Key. An interface that all components of a CoroutineContext must implement. coroutineContext is a container of metadata that defines how a coroutine should behave. Every coroutine runs with a context - a powerful set of tools bundled together to manage execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines always execute in some context represented by a value of the CoroutineContext type.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Coroutine Context consists of elements that are stored in a stack-like structure, where the elements at the top of the stack have higher precedence. The elements are represented by instances of the CoroutineContext interface, which provides methods to access and manipulate the elements in the context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A ‘CoroutineContext’ is defined in the scope in which we start the coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is a collection of several ‘context’ elements ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ol {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Dispatcher - (Which thread to run on,  Control where the coroutine runs)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Job - (control the lifecycle of coroutine, Defines the lifecycle, enabling structured concurrency and cancellation)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "CoroutineExceptionHandler (Error handler) - (handle errors when a coroutine throws an exception,  Handles uncaught exceptions in a centralized way)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "CoroutineName - (Used for debugging, logging or adding custom metadata)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dispatcher(CoroutineDispatcher)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Specifies the thread or threads on which the coroutine runs. The dispatcher determines the thread or thread pool on which a coroutine runs.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Common dispatchers include Dispatchers.Default for CPU-intensive work, Dispatchers.IO for I/O-bound work, and Dispatchers.Main for updating the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Job",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Represents the lifecycle of the coroutine and allows to control the execution of the coroutine, cancel it, and check its status. ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A job represents a unit of work that can be canceled. When a coroutine is launched, it returns a reference to its job, which can be used to cancel the coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Error handler (CoroutineExceptionHandler)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Handles uncaught exceptions that occur in the coroutine. It allows you to define a custom handler for exceptions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "By default, unhandled exceptions in a coroutine are propagated to the parent coroutine or to the main thread.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                        class AppViewModel(
                        ) : ViewModel() {

                            private val exceptionHandler = CoroutineExceptionHandler { context, throwable ->
                                throwable.printStackTrace()
                                printInDebug("Exception: $throwable")
                            }
                            
                            private fun fetchDataAndTrack() = viewModelScope.launch(exceptionHandler) 
                            {
                            
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    fun main() = runBlocking {

                       val handler = CoroutineExceptionHandler { context, exception ->
                                println(exception.message)
                            }
                       val customScope = CoroutineScope(handler)
                       val job = customScope.launch {
                                riskyOperation()
                            }
                            job.join()
                            println("Main: Completed")
                    }

                    suspend fun riskyOperation() {
                        delay(1000)
                        throw IllegalStateException("Something went wrong!")
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Error handler (Propagating Errors)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In structured concurrency, when a child coroutine throws an exception, the parent coroutine is cancelled along with all its children. This helps maintain consistency and ensures that errors are propagated correctly.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                    fun main() {
                        runBlocking {
                            val handler = CoroutineExceptionHandler { context, exception ->
                                println(exception.message) //Caught exception
                            }
                            val customScope = CoroutineScope(handler)
                            val parentJob = customScope.launch {
                                val childJob = launch {
                                    riskyOperation()
                                }
                                childJob.join()
                            }
                            parentJob.join()
                            println("Main: Completed")
                        }
                    }

                    suspend fun riskyOperation() {
                        delay(1000)
                        throw Exception("Something went wrong!")
                    }

                    """.trimIndent()
                )

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineName",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Provides a name for the coroutine, which can be useful for debugging and logging purposes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Defines the scope of a coroutine and is a special case of CoroutineContext. It provides a convenient way to launch coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Job",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Job is a handle to a coroutine. Job is interface that represent a state of Coroutine. It allows starting, pausing, canceling, and monitoring the state of an asynchronous operation. When a coroutine is started, a Job is created to manage it. State is calculated by type-check of internal property of JobSupport. JobSupport, which is implementation of Job, decides its state by type-check.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As the name implies, this context element represents a background task or a job. Jobs are cancellable by calling the cancel() function on it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It has isActive, isCompleted, isCancelled properties, and children Job as Sequence, which means that it implements “structured concurrency”.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineBuilder launch makes StandaloneCoroutine which is child class of AbstractCoroutine, which is implementation of JobSupport. And then, start the coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It offers functionalities to manage and control the coroutine’s lifecycle. We can also pass a Job to aCoroutineScope to further manage its lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each coroutine that we create with ‘launch’ or ‘async’ returns a Jobinstance that uniquely identifies the coroutine and manages its lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "All the information about a car, like its state (running, stopped) and how it behaves (can be controlled remotely), is stored in the CoroutineContext.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This context is made up of smaller tools called CoroutineContext.Elements.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Structure complex asynchronous operations using parent-child relationships.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "There are 2 type of Jobs",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Job",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "If one child coroutine fails, will make the parent fail, causing the sibling coroutines to fail.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SupervisorJob",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When you need more granular control over failure handling, the SupervisorJob comes into play. If one child coroutine fails, won't make the parent fail and the siblings can continue until they complete.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "SupervisorJob allows child coroutines to fail without cancelling the parent or other children.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "This is particularly useful when the failure of one operation shouldn’t disrupt others. Think of user interfaces, where one task (e.g., loading an image) failing shouldn’t stop other unrelated tasks (e.g., loading text).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                    val customScope = CoroutineScope(SupervisorJob())
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Actions with Job",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Checking State",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You can query the current state of the Job (e.g., New, Active, Completed, Cancelled) to understand its execution status.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancellation",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You can cancel a Job to forcefully stop the corresponding coroutine's execution.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency Management",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You can establish relationships between Job instances:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "join(): Suspends the current coroutine until another Job finishes execution (suspends the coroutine until the job it’s associated with completes).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Parent-Child Relationship: Coroutine builders like launch can create parent-child relationships, where a child's completion is required for the parent to finish.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Job States",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "New",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The initial state after creating a Job.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Active",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The coroutine is actively executing.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Completing",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The coroutine is finishing its work and waiting for all its children to complete.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "join",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "suspending function that waits for the coroutine to fully complete. This ensures that the coroutine has finished all of its cleanup work after the cancellation request.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "After you cancel a job, the coroutine may still have pending cleanup tasks (such as releasing resources, finishing its current task, etc.). join() ensures that the coroutine finishes all of that before the program continues.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancelled",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The coroutine’s execution was forcefully stopped.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "requests cancellation of the coroutine. This doesn't immediately stop the coroutine's execution; instead, it marks it as cancelled.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the coroutine is using suspension points like delay(), it will check if it is cancelled at these points and stop execution gracefully. If the coroutine doesn't have suspension points (e.g., it's doing CPU-bound work in a tight loop), the coroutine won't stop immediately.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    //Creating a Job Manually

                    val job = Job()
                    val scope = CoroutineScope(Dispatchers.I0 + job)
                    scope.launch {
                        // Execute some task
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Job Join

                    val job = scope.launch {
                        delay (2000)
                        println ("Task completed")
                    }

                    runBlocking {
                        job.join()
                        println("Continuing execution")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Job Control with AtomicReference

                    import kotlinx.coroutines.*
                    import java.util. concurrent.atomic.AtomicReference

                    class JobManager(private val coroutineScope: CoroutineScope) {
                        private val job = AtomicReference<Job?> (null)
                        fun startJob() {
                            if (job. get == null) {
                                job.set(coroutineScope.launch {
                                // Execute asynchronous task
                                delay (3000)
                                println(" Job completed")
                            })
                        }
                    }

                    fun cancelJob() {
                        job.get()?.cancel()
                        job.set(null)
                    }

                    fun onDestroy() {
                        cancelJob()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Deferred Job and How it pass the result to parent Coroutine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Deferred is interface that implements Job. It allows you to get result of Coroutine block using await() method. Deferred is created when you create Coroutine using async CoroutineBuilder.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "async makes DeferredCoroutine , which is implementation of Deferred. The await method is implemented by JobSupport.awaitInternal() method.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "awaitInternal method checks state of Coroutine in loop. If the state is CompletedExceptionally , call recoverAndThrow() , otherwise break the loop and “suspend” the Coroutine by calling awaitSuspend().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In awaitSuspend(), the caller’s Continuation is intercepted(convert to Runnable for Dispatchers), and create new AwaitContinuation: val cont = AwaitContinuation(uCont.intercepted(), this)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "And then, register callback to resume caller Coroutine after async block is finished if the caller Coroutine is suspended:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "invokeOnCompletion(ResumeAwaitOnCompletion(cont).asHandler) . This code makes possible Coroutine restart when a required return value is ready.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Finally, Continuation.getResult() returns COROUTINE_SUSPENDED which makes Continuation finish.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }


            SpanText(
                text = "Job vs Deferred(async)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Job",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Job is a cancellable thing with a lifecycle that culminates in its completion. It is the result of launching a coroutine with launch builder.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It doesn’t return any result. You can use a Job to manage the lifecycle of a coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For example, you can cancel a job, you can wait for a job to complete, and you can check if a job is completed or cancelled.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Deferred",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Deferred is a non-blocking cancellable future — it is a reference to the deferred computation (hence the name). ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is a Job with a result.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is the result of launching a coroutine with async builder.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It returns a result that can be awaited. Awaiting does not block the current thread but suspends the coroutine until the computation is done.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }


            SpanText(
                text = "CoroutineExceptionHandler (Cancelling Coroutines) (Handling Exception)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For top-level coroutine error handling, CoroutineExceptionHandler can catch unhandled exceptions globally. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = $$"""
                        val exceptionHandler = CoroutineExceptionHandler { _, exception ->
                            println("Caught: ${exception.message}")
                        }

                        val scope = CoroutineScope(Dispatchers.IO + exceptionHandler)
                        scope.launch {
                            throw RuntimeException("Something went wrong")
                        }


                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Any exception thrown inside launch will be caught by CoroutineExceptionHandler.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unlike threads, coroutines in Kotlin are helpful. This means coroutines don’t just stop executing the moment you cancel them — they need to check periodically if they have been cancelled and respond accordingly. In Kotlin, coroutine cancellation is cancellable but not carefully, so the coroutine is responsible for its own cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This context element will help us catch the exceptions, so we can show a proper error instead of letting the exception propagate. This won't change the cancellation behavior explained in the Job section.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine this as a safety feature. If something goes wrong with your car (an error occurs), this handler catches the error and prevents the car from malfunctioning (crashing).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Scenarios for Cancellation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Exceptions: Network issues, resource limitations, or unexpected errors might require cancelling a coroutine.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "User Interaction: User actions like scrolling or navigating away might make an ongoing coroutine (like image download) irrelevant, requiring cancellation.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Traditional Exception Handling",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Uncaught exceptions in traditional programming can crash the entire program.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutine Exception Handling",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Suspending behavior allows exceptions to be handled within a coroutine using mechanisms like try-catch.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Exceptions can still be propagated to the main thread using specific mechanisms like CoroutineExceptionHandler.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancellation with Job.cancel()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The cancel method on a Job allows you to forcefully stop the corresponding coroutine's execution.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The coroutines library intelligently handles dependencies:",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Cancelling a Job automatically cancels its children.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "A parent Job gets cancelled if one of its children is cancelled or encounters an exception.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }

                    Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancelling CoroutineScope",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "coroutineScope to manage multiple coroutines, you can cancel all coroutines launched within the scope by cancelling the scope itself",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            KotlinCode(
                code = """
                    //job cancel and join

                    fun main() = runBlocking {
                        val job = launch {
                            repeat(1000) { i ->
                                println(i) //Job: I'm working
                                delay(500)
                            }
                        }

                        delay(1300)
                        println("Main: I’m tired of waiting!")
                        job.cancel()
                        job.join()
                        println("Main: Now I can quit.")
                    }

                    /*
                    output
                    Job: I'm working 0 ...
                    Job: I'm working 1 ...
                    Job: I'm working 2 ...
                    Main: I’m tired of waiting!
                    Main: Now I can quit.
                    */
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Cancelling CoroutineScope

                    val scope = CoroutineScope(Dispatchers.Default)
                    val job1 = scope.launch {
                        // Coroutine 1
                    }
                    val job2 = scope.launch {
                        // Coroutine 2
                    }

                    // Cancel all coroutines launched within the scope
                    scope.cancel()
                    """.trimIndent()
            )

            SpanText(
                text = "CancellationException",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancellation flows “absorbed” by a generic catch.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines that refuse to die as expected.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // ❌ Treat cancellation as if it were a business mistake
                    suspend fun uploadPendingEvents(
                        uploader: EventsUploader
                    ) {
                        try {
                            uploader.sendAllPending()
                        } catch (e: Exception) {
                            logUploadFailure(e)
                            notifyUserUploadFailed()
                        }
                    }
                    
                    suspend fun uploadPendingEvents(
                        uploader: EventsUploader
                    ) {
                        try {
                            uploader.sendAllPending()
                        } catch (e: CancellationException) {
                            // ✅ Letting cancellation spread as is
                            throw e
                        } catch (e: Exception) {
                            logUploadFailure(e)
                            notifyUserUploadFailed()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "cancel() - The Non-Blocking Signal",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The cancel() function simply sends a cancellation signal to the coroutine's associated Job.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Behavior: It marks the Job as cancelling and returns immediately. It does not wait for the coroutine to finish any cleanup or completely terminate.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Result: The coroutine will throw a CancellationException at the next cancellation check point (e.g., any suspending function like delay(), yield(), or coroutine builders).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use Case: When you want to initiate cancellation quickly and you don’t care exactly when the coroutine finishes, or if you plan to handle the final waiting later.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = $$"""
                    import kotlinx.coroutines.*
                    import kotlin.time.Duration.Companion.milliseconds

                    fun main() = runBlocking {
                        val job = launch(Dispatchers.Default) {
                            try {
                                var counter = 0
                                while (isActive) {
                                    // Cancellation check point (delay)
                                    delay(100.milliseconds) 
                                    println("Processor is running, cycle ${++counter}...")
                                }
                            } catch (e: CancellationException) {
                                // A coroutine should always perform cleanup here!
                                println("❌ Processor caught CancellationException. Finalizing resources.")
                            } finally {
                                // 'finally' is also a good place for guaranteed cleanup
                                println("✅ Processor job is finished (Cleanup complete).")
                            }
                        }

                        delay(300.milliseconds)
                        
                        // --- The key line ---
                        job.cancel() 
                        
                        // This line executes immediately after the signal is sent.
                        println("Main coroutine: Signal sent! Not waiting for processor.")
                        
                        // If we didn't add the delay below, main might finish before job does!
                        delay(500.milliseconds) 
                    }
                    /*
                    Expected (Enhanced) Output:
                    Processor is running, cycle 1...
                    Processor is running, cycle 2...
                    Processor is running, cycle 3...
                    Main coroutine: Signal sent! Not waiting for processor.
                    ❌ Processor caught CancellationException. Finalizing resources.
                    ✅ Processor job is finished (Cleanup complete).
                    */
                    """.trimIndent()
            )

            SpanText(
                text = "How Does Cancellation Work?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines check for cancellation at suspension points like delay(), yield(), or any other suspending function. When a coroutine reaches a suspension point, it automatically checks if it has been cancelled and then throws a CancellationException.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Here, isActive is a property that checks if the coroutine is still running or has been cancelled. If the coroutine is no longer active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    fun main() = runBlocking {
                        val job = launch {
                            repeat(1000) { i ->
                                println(i) //Job: I'm working
                                if (isActive) {
                                    delay(500)
                                } else {
                                    println("Job: I've been cancelled")
                                }
                            }
                        }

                        delay(1500
                        println("Main: Cancelling job!")
                        job.cancelAndJoin()
                        println("Main: Job is cancelled.")
                    }

                    /*
                    output
                    Job: I'm working 0 ...
                    Job: I'm working 1 ...
                    Job: I'm working 2 ...
                    Main: Cancelling job!
                    Main: Job is cancelled.
                    */
                    """.trimIndent()
            )

            SpanText(
                text = "cancelAndJoin() - The Suspending, Guaranteed Stop",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The cancelAndJoin() is a suspending extension function that combines the two actions: cancel() followed by join(). ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It sends the cancellation signal (cancel()). It suspends the calling coroutine until the target coroutine has fully terminated (either successfully, canceled, or failed).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Guarantees: When cancelAndJoin() returns, you are guaranteed that the target coroutine has finished its execution and all its resources have been released.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                    import kotlin.time.Duration.Companion.milliseconds

                    fun main() = runBlocking {
                        val serverConnectionJob = launch {
                            // Assume this simulates an open network connection
                            println("📡 Server connection established.")
                            try {
                                delay(1000L) // Simulate long-running network operation
                            } catch (e: CancellationException) {
                                println("❌ Connection job detected cancellation.")
                            } finally {
                                // CRITICAL: Must ensure the connection is closed
                                println("⬇️ Closing network socket and releasing resources.")
                            }
                        }

                        delay(200.milliseconds)
                        println("Main coroutine: Server job is running...")

                        // --- The key line ---
                        serverConnectionJob.cancelAndJoin() // Main coroutine SUSPENDS here

                        // This line only executes AFTER the serverConnectionJob's 'finally' block completes.
                        println("Main coroutine: Socket confirmed closed. Safe to proceed with system shutdown.") 
                    }
                    /*
                    Expected (Enhanced) Output:
                    📡 Server connection established.
                    Main coroutine: Server job is running...
                    ❌ Connection job detected cancellation.
                    ⬇️ Closing network socket and releasing resources.
                    Main coroutine: Socket confirmed closed. Safe to proceed with system shutdown.
                    */
                    """.trimIndent()
            )

            SpanText(
                text = "CoroutineName",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Assign a specific name to a coroutine can be helpful for debugging purposes. This element can help with this.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val coroutineScope = CoroutineScope(Job() + CoroutineName("abc1"))

                    coroutineScope.launch {
                        println(Thread.currentThread().name)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "combine multiple context + symbol",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    private val coroutineExceptionHandler = CoroutineExceptionHandler { _, exception ->
                        exception.printStackTrace()
                    }

                    private val job = SupervisorJob()
                    private val viewModelScope = CoroutineScope(Dispatchers.Main + coroutineExceptionHandler + job)

                    viewModelScope.launch {
                        //Logic
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val exceptionHandler = CoroutineExceptionHandler { _, throwable ->
                      println(throwable)
                    }

                    fun controlSteering(direction: String) = launch(Dispatchers.Default + exceptionHandler) {

                      // Simulate a potential error (e.g., invalid direction)
                      if (direction == "Fly") throw IllegalArgumentException("Cars can't fly!")
                        println("Steering successful!")
                    }

                    // Start controlling (with potential for errors)
                    controlSteering("Left")
                    """.trimIndent()
            )
        }
    }
}

//https://medium.com/@vijaykantkaushik/mastering-kotlin-coroutines-understanding-the-core-building-blocks-part-1-2cf97a0ef434
//https://medium.com/@eduardo.santos.gualberto/kotlin-how-are-coroutines-canceled-cccd3862e577
//https://leonelmenaiadev.medium.com/how-cancellationexception-breaks-your-rxjava-and-coroutine-bridge-fbdc41544874